// src/app/pages/orders/PickupPersonModal.js

import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Grid, Button, TextField, Typography, Box
} from '@mui/material';
import { hasValidRutFormat, formatRut } from 'app/utils/rut';

const emptyForm = { name: '', rut: '', plate: '', phone: '' };

// El nombre se muestra en la app del conductor, que tiene poco espacio.
const NAME_MAX_LENGTH = 20;

const PickupPersonModal = ({ open, onClose, initialData, onSave, onRemove, saving }) => {
  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState(false);
  const [rutTouched, setRutTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        name: initialData?.name || '',
        rut: initialData?.rut || '',
        plate: initialData?.plate || '',
        phone: initialData?.phone || '',
      });
      setTouched(false);
      setRutTouched(false);
    }
  }, [open, initialData]);

  const hasExisting = Boolean(initialData?.name);

  const rutEmpty = !form.rut.trim();
  const rutInvalid = !hasValidRutFormat(form.rut);
  // Mostrar el error en rojo cuando el usuario ya salió del campo (blur) o
  // intentó guardar. Al escribir algo inválido también se marca en vivo.
  const showRutError = (rutTouched || touched) && rutInvalid;

  const nameError = touched && !form.name.trim();
  const rutError = showRutError;
  const rutHelperText = showRutError
    ? (rutEmpty ? 'El RUT es obligatorio' : 'RUT inválido')
    : ' ';
  const canSave = form.name.trim() && hasValidRutFormat(form.rut) && !saving;

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleRutBlur = () => {
    setRutTouched(true);
    if (form.rut && hasValidRutFormat(form.rut)) {
      setForm((p) => ({ ...p, rut: formatRut(p.rut) }));
    }
  };

  const handleSave = () => {
    setTouched(true);
    if (!form.name.trim() || !hasValidRutFormat(form.rut)) return;

    onSave({
      name: form.name.trim(),
      rut: formatRut(form.rut),
      plate: form.plate.trim(),
      phone: form.phone.trim(),
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '480px', maxWidth: '90%' } }}
    >
      <DialogTitle>
        <Typography variant="h2" color="primary">
          {hasExisting ? 'Editar persona que retira' : 'Persona que retira'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Nombre conductor"
              value={form.name}
              onChange={handleChange('name')}
              required
              fullWidth
              error={nameError}
              inputProps={{ maxLength: NAME_MAX_LENGTH }}
              helperText={
                nameError
                  ? 'El nombre es obligatorio'
                  : `${form.name.length}/${NAME_MAX_LENGTH}`
              }
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="RUT"
              value={form.rut}
              onChange={handleChange('rut')}
              onBlur={handleRutBlur}
              required
              fullWidth
              placeholder="12.345.678-9"
              error={rutError}
              helperText={rutHelperText}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Patente vehículo"
              value={form.plate}
              onChange={handleChange('plate')}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Teléfono"
              value={form.phone}
              onChange={handleChange('phone')}
              fullWidth
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        {hasExisting && (
          <Box sx={{ mr: 'auto' }}>
            <Button color="error" onClick={onRemove} disabled={saving}>
              Quitar
            </Button>
          </Box>
        )}
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="success" onClick={handleSave} disabled={!canSave}>
          {saving ? 'Guardando...' : 'Guardar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PickupPersonModal;
